import { useMemo, useState } from 'react';
import TabBar from '@alltrails/shared/denali/components/TabBar';
import Expandable from '@alltrails/shared/components/Expandable';
import LinkedInIcon from '@alltrails/shared/components/socialIcons/LinkedInIcon';
import Key from 'types/Key';
import useMobileWidth from 'hooks/useMobileWidth';
import useDepartmentHistory from './hooks/useDepartmentHistory';
import { Department, Posting } from '../types';
import getDepartmentLabel from './utils/getDepartmentLabel';
import JobPosting from './JobPosting';
import JobsHeader from './JobsHeader';

import * as styles from './styles/styles.module.scss';

export type JobPostingsProps = {
  postings: Partial<Record<Department, Posting[]>>;
};

const constructTab = (tab: Department, count: number) => ({
  id: tab,
  label: getDepartmentLabel(tab, count),
  testId: `open-positions-tab-${tab}`
});

const departments = Object.keys(Department);

const JobPostings = ({ postings }: JobPostingsProps): JSX.Element => {
  const { initialDepartment, updateDepartmentHistory } = useDepartmentHistory();
  const isMobileView = useMobileWidth(640);
  const tabOptions = useMemo(
    () =>
      departments
        .map((tab: Department) => {
          let numPostings = postings[tab]?.length;
          if (tab === Department.All) {
            numPostings = Object.values(postings).reduce((count: number, depPostings: Posting[]) => count + depPostings.length, 0);
          }
          return numPostings || tab === Department.All ? constructTab(tab, numPostings) : null;
        })
        .filter(item => !!item),
    [postings]
  );
  const [showAllPostings, setShowAllPostings] = useState(!isMobileView);
  const [selectedTab, setSelectedTab] = useState(initialDepartment || tabOptions[0].id);

  const handleTabChange = (tab: Department) => {
    setSelectedTab(tab);
    setShowAllPostings(false);
    updateDepartmentHistory(tab);
  };

  const onLoadAll = () => setShowAllPostings(true);
  const enterKeyHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.Enter) {
      onLoadAll();
    }
  };

  const renderPostings = (): JSX.Element | JSX.Element[] => {
    const sortedDepartments = Object.keys(postings).sort();
    let renderedPostings: JSX.Element[] = [];
    sortedDepartments.forEach((departmentKey: Department) => {
      const departmentPostings = postings[departmentKey]
        .map((posting: Posting) => {
          const shouldRender = selectedTab === Department.All || selectedTab === departmentKey;
          return shouldRender ? <JobPosting key={posting.url} posting={posting} department={departmentKey} /> : null;
        })
        .filter(posting => !!posting);
      if (departmentPostings.length) {
        renderedPostings = renderedPostings.concat(departmentPostings);
      }
    });

    if (isMobileView && !showAllPostings) {
      renderedPostings = renderedPostings.slice(0, 4);
    }

    return <div className={styles.allPostings}>{renderedPostings}</div>;
  };

  return (
    <div className={styles.jobsContainer}>
      <JobsHeader />
      <div className={styles.tabsContainer}>
        <TabBar activeTab={selectedTab} className={styles.tabBar} onChange={handleTabChange} tabs={tabOptions} testId="open-positions-tab-bar" />
        <div className={styles.linkedInIcon}>
          <LinkedInIcon openInNewTab />
        </div>
      </div>
      {renderPostings()}
      {isMobileView && !showAllPostings && (
        <div className={styles.expandableWrapper} tabIndex={showAllPostings ? -1 : 0} role="button" onKeyPress={enterKeyHandler} onClick={onLoadAll}>
          <Expandable title="Load all">{null}</Expandable>
        </div>
      )}
    </div>
  );
};

export default JobPostings;
